import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { getUser } from "@/lib/user";
import { VILLES_QUARTIERS_CI, AUTRE_QUARTIER, COMMUNES_ABIDJAN, VILLES_INTERIEUR } from "@/lib/villesQuartiers";
import "./FodindinLoveRegister.css";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api/fodindinlove/register-step2`;

const ETHNICITES = ["Africaine", "Nord-africaine / Maghrébine", "Européenne", "Asiatique", "Métisse", "Autre", "Préfère ne pas dire"];
const EDUCATIONS = ["Collège", "Lycée / BAC", "BAC+2 (BTS/DUT)", "Licence", "Master", "Doctorat", "Autre"];
const PAYS = [
  ["CI", "Côte d'Ivoire"], ["SN", "Sénégal"], ["ML", "Mali"], ["BF", "Burkina Faso"],
  ["TG", "Togo"], ["BJ", "Bénin"], ["GN", "Guinée"], ["NE", "Niger"], ["AUTRE", "Autre pays"],
];
const DISTRICTS = [
  "Abidjan", "Bas-Sassandra", "Comoé", "Denguélé", "Gôh-Djiboua", "Lacs", "Lagunes",
  "Montagnes", "Sassandra-Marahoué", "Savanes", "Vallée du Bandama", "Woroba",
  "Yamoussoukro", "Zanzan", "Hors Côte d'Ivoire",
];

export default function FodindinLoveStep2() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const user = getUser();
  const [form, setForm] = useState({
    age: "", ethnicite: "", taille: "", education: "", profession: "",
    pays: "CI", ville: "", quartier: "", quartierAutre: "", region: "", pretAVoyager: null,
  });
  const [status, setStatus] = useState({ loading: false, error: "" });

  const update = (field, value) => setForm((c) => ({ ...c, [field]: value }));
  const complet = form.age && form.taille && form.region && form.pretAVoyager !== null;

  async function handleSubmit(event) {
    event.preventDefault();
    if (!complet) return;
    setStatus({ loading: true, error: "" });
    try {
      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          profile_id: user?.id || "",
          age: Number(form.age),
          ethnicite: form.ethnicite || null,
          taille: Number(form.taille),
          education: form.education || null,
          profession: form.profession || null,
          pays: form.pays,
          ville: form.ville || null,
          quartier: form.quartier === AUTRE_QUARTIER ? (form.quartierAutre.trim() || null) : (form.quartier || null),
          region: form.region,
          pretAVoyager: form.pretAVoyager === "oui",
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Impossible de continuer.");
      navigate(params.get("next") || data.nextStep || "/abonnement?cat=love");
    } catch (error) {
      setStatus({ loading: false, error: error.message || "Une erreur est survenue." });
    }
  }

  const selectCls = { width: "100%", padding: 12, border: "1px solid var(--fl-border)", borderRadius: 11, background: "var(--fl-lilac)", color: "var(--fl-ink)", font: "inherit" };
  const labelCls = { display: "block", fontSize: 13, fontWeight: 700, marginBottom: 8 };
  const champCls = { marginBottom: 20 };
  const facultatif = <span style={{ fontWeight: 400, color: "#999" }}> (facultatif)</span>;

  return (
    <main className="fl-page" data-testid="fodindinlove-step2-page">
      <header className="fl-header">
        <Link to="/" className="fl-brand">
          <span className="fl-brand-heart">♥</span>
          <span>Fodindin<span>Love</span></span>
        </Link>
        <span className="fl-login">Étape 2/3</span>
      </header>

      <section className="fl-card">
        <div className="fl-kicker">Vos informations</div>
        <h1 style={{ fontSize: "clamp(26px, 4vw, 34px)" }}>Complétez votre profil</h1>
        <p className="fl-intro">
          Vous contrôlez votre confidentialité et pouvez mettre à jour votre profil à tout moment.
        </p>

        {!user ? (
          <div>
            <p className="fl-error" role="alert">Crée d'abord ton compte ou connecte-toi pour continuer.</p>
            <Link to="/inscription" className="fl-submit" style={{ display: "block", textAlign: "center", textDecoration: "none", marginTop: 14 }}>
              Créer mon compte →
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} data-testid="fl2-form">
            <div style={champCls}>
              <label style={labelCls}>Âge</label>
              <select style={selectCls} value={form.age} onChange={(e) => update("age", e.target.value)} data-testid="fl2-age">
                <option value="">— Sélectionner —</option>
                {Array.from({ length: 53 }, (_, i) => 18 + i).map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
              <p className="fl-privacy-note" style={{ marginTop: 6 }}>Nous n'acceptons que les membres âgés de 18 ans ou plus.</p>
            </div>

            <div style={champCls}>
              <label style={labelCls}>Ethnicité{facultatif}</label>
              <select style={selectCls} value={form.ethnicite} onChange={(e) => update("ethnicite", e.target.value)} data-testid="fl2-ethnicite">
                <option value="">— Sélectionner —</option>
                {ETHNICITES.map((e) => <option key={e}>{e}</option>)}
              </select>
            </div>

            <div style={champCls}>
              <label style={labelCls}>Taille</label>
              <select style={selectCls} value={form.taille} onChange={(e) => update("taille", e.target.value)} data-testid="fl2-taille">
                <option value="">— Sélectionner —</option>
                {Array.from({ length: 71 }, (_, i) => 140 + i).map((cm) => <option key={cm} value={cm}>{cm} cm</option>)}
              </select>
            </div>

            <div style={champCls}>
              <label style={labelCls}>Niveau d'éducation{facultatif}</label>
              <select style={selectCls} value={form.education} onChange={(e) => update("education", e.target.value)} data-testid="fl2-education">
                <option value="">— Sélectionner —</option>
                {EDUCATIONS.map((e) => <option key={e}>{e}</option>)}
              </select>
            </div>

            <div style={champCls}>
              <label style={labelCls}>Profession{facultatif}</label>
              <input
                type="text"
                style={selectCls}
                placeholder="Ex : Commerçant(e), Étudiant(e), Ingénieur(e)..."
                value={form.profession}
                onChange={(e) => update("profession", e.target.value)}
                data-testid="fl2-profession"
              />
            </div>

            <div style={champCls}>
              <label style={labelCls}>Pays ou région</label>
              <select style={selectCls} value={form.pays} onChange={(e) => update("pays", e.target.value)} data-testid="fl2-pays">
                {PAYS.map(([code, nom]) => <option key={code} value={code}>{nom}</option>)}
              </select>
            </div>

            <div style={champCls}>
              <label style={labelCls}>Ville ou commune{facultatif}</label>
              <select
                style={selectCls}
                value={form.ville}
                onChange={(e) => setForm((c) => ({ ...c, ville: e.target.value, quartier: "", quartierAutre: "" }))}
                data-testid="fl2-ville"
              >
                <option value="">— Sélectionner —</option>
                <optgroup label="Abidjan (communes)">
                  {COMMUNES_ABIDJAN.map((v) => <option key={v} value={v}>{v.replace("Abidjan - ", "Abidjan — ")}</option>)}
                </optgroup>
                <optgroup label="Villes de l'intérieur">
                  {VILLES_INTERIEUR.map((v) => <option key={v} value={v}>{v}</option>)}
                </optgroup>
              </select>
              {form.ville && (
                <select
                  style={{ ...selectCls, marginTop: 10 }}
                  value={form.quartier}
                  onChange={(e) => setForm((c) => ({ ...c, quartier: e.target.value, quartierAutre: "" }))}
                  data-testid="fl2-quartier"
                >
                  <option value="">— Quartier (facultatif) —</option>
                  {(VILLES_QUARTIERS_CI[form.ville] || []).map((q) => <option key={q}>{q}</option>)}
                </select>
              )}
              {form.quartier === AUTRE_QUARTIER && (
                <input
                  type="text"
                  style={{ ...selectCls, marginTop: 10 }}
                  placeholder="Précisez votre quartier..."
                  value={form.quartierAutre}
                  onChange={(e) => update("quartierAutre", e.target.value)}
                  data-testid="fl2-quartier-autre"
                />
              )}
            </div>

            <div style={champCls}>
              <label style={labelCls}>Région ou district</label>
              <select style={selectCls} value={form.region} onChange={(e) => update("region", e.target.value)} data-testid="fl2-region">
                <option value="">— Sélectionner —</option>
                {DISTRICTS.map((d) => <option key={d}>{d}</option>)}
              </select>
            </div>

            <div style={champCls}>
              <label style={labelCls}>Prêt(e) à voyager ou vous déplacer pour rencontrer quelqu'un ?</label>
              <div className="fl-options" style={{ gridTemplateColumns: "1fr 1fr" }}>
                {["oui", "non"].map((v) => (
                  <label key={v} className={`fl-option ${form.pretAVoyager === v ? "selected" : ""}`} style={{ justifyContent: "center" }}>
                    <input
                      type="radio"
                      name="pret"
                      checked={form.pretAVoyager === v}
                      onChange={() => update("pretAVoyager", v)}
                      data-testid={`fl2-pret-${v}`}
                    />
                    <span style={{ textTransform: "capitalize" }}>{v}</span>
                  </label>
                ))}
              </div>
            </div>

            {status.error && <p className="fl-error" role="alert" data-testid="fl2-error">{status.error}</p>}

            <button className="fl-submit" type="submit" disabled={!complet || status.loading} data-testid="fl2-submit" style={!complet ? { background: "#ccc", boxShadow: "none", cursor: "not-allowed" } : {}}>
              {status.loading ? "Enregistrement…" : "CONTINUER (2/3)"}
            </button>
          </form>
        )}
      </section>

      <footer className="fl-footer">
        Des rencontres authentiques, à ton rythme. <span>♥</span>
      </footer>
    </main>
  );
}
